/**
 * BP-21 T5: the five finishes, as lighting rather than as colour swatches.
 *
 * A shade is not a colour. Two bottles with the same pigment and a different
 * finish look nothing alike on a hand, and a try-on that renders both as a
 * flat fill of `#C2185B` is the kind that makes a client distrust the whole
 * screen. What separates them is almost entirely how they handle light: how
 * sharp the highlight is, how much of it there is, whether it is one
 * highlight or ten thousand, and whether it smears along one axis.
 *
 * So each finish here is a small set of physical parameters, and one shading
 * function reads all five. There is no per-finish branch in the renderer and
 * no fifth code path to forget when a sixth finish is added.
 *
 * **§14 item 1 is applied**: cream and matte ship enabled; glitter, chrome
 * and cat-eye are built and behind a setting, off by default. `DEFAULT_ON` is
 * that decision, in one place, readable by the screen and by a test.
 *
 * **Linear light, sRGB storage.** BP-12 stores `srgb_hex`, which is encoded
 * with a ~2.2 gamma; adding a highlight to it directly brightens a dark shade
 * far too much and washes a pale one out. Every calculation here happens in linear
 * space and the result is encoded back on the way out. This is the single
 * difference between a render that looks like nail polish and one that looks
 * like a coloured rectangle.
 *
 * **No randomness.** Glitter's sparkle comes from a hash of the pixel
 * coordinate, so the same nail sparkles the same way in two consecutive
 * frames. `Math.random` per pixel per frame is a nail that boils.
 */

import type { Point } from './geometry';

/** The five §10 T5 names. `cat_eye` is snake-cased to match the api's style. */
export type FinishName = 'cream' | 'matte' | 'glitter' | 'chrome' | 'cat_eye';

/** All five, in the order the compare strip offers them. */
export const FINISHES: readonly FinishName[] = ['cream', 'matte', 'glitter', 'chrome', 'cat_eye'];

/**
 * §14 item 1: what is on without anybody changing a setting.
 *
 * Cream and matte are every salon's shelf. The other three are striking and
 * are the three whose render is hardest to get right on a cheap phone, so
 * they are a decision a salon makes once it has seen them.
 */
export const DEFAULT_ON: readonly FinishName[] = ['cream', 'matte'];

/** How one finish handles light. */
export interface Finish {
  /** Strength of the specular highlight, 0–1. */
  specular: number;
  /** Width of it. Low is a tight glint, high is a broad sheen. */
  roughness: number;
  /** How much of the base colour survives: chrome keeps almost none. */
  albedo: number;
  /** Sparkles per unit area. Zero for everything but glitter. */
  sparkleDensity: number;
  /** How much the highlight is stretched along the nail's axis (cat-eye). */
  anisotropy: number;
}

/**
 * The five, as numbers.
 *
 * They are judgements, not measurements — nobody has a goniophotometer here —
 * but they are judgements about the right quantity. `matte` is `cream` with
 * the highlight taken away and spread out; `chrome` keeps a tenth of the
 * pigment because a chrome finish is mostly a mirror; `cat_eye` is a cream
 * whose highlight is squeezed into a band.
 */
export const FINISH: Readonly<Record<FinishName, Finish>> = {
  cream: { specular: 0.45, roughness: 0.18, albedo: 1, sparkleDensity: 0, anisotropy: 0 },
  matte: { specular: 0.06, roughness: 0.85, albedo: 1, sparkleDensity: 0, anisotropy: 0 },
  glitter: { specular: 0.35, roughness: 0.3, albedo: 0.9, sparkleDensity: 0.04, anisotropy: 0 },
  chrome: { specular: 0.95, roughness: 0.04, albedo: 0.12, sparkleDensity: 0, anisotropy: 0 },
  cat_eye: { specular: 0.7, roughness: 0.12, albedo: 0.95, sparkleDensity: 0, anisotropy: 0.92 },
};

/** Linear-light RGB, each channel 0–1. */
export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** One channel, sRGB-encoded 0–1 → linear 0–1 (IEC 61966-2-1). */
export function srgbToLinear(channel: number): number {
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
}

/** The inverse. Clamped: a highlight can take a channel above 1. */
export function linearToSrgb(channel: number): number {
  const clamped = Math.min(Math.max(channel, 0), 1);
  return clamped <= 0.0031308 ? clamped * 12.92 : 1.055 * clamped ** (1 / 2.4) - 0.055;
}

/**
 * BP-12's `srgb_hex` (six hex digits, no `#`) as linear RGB.
 *
 * Returns `null` rather than black for anything else. A malformed colour
 * rendered as black is a shade the client sees and believes.
 */
export function parseShade(hex: string): Rgb | null {
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) {
    return null;
  }
  const value = Number.parseInt(hex, 16);
  return {
    r: srgbToLinear(((value >> 16) & 0xff) / 255),
    g: srgbToLinear(((value >> 8) & 0xff) / 255),
    b: srgbToLinear((value & 0xff) / 255),
  };
}

/** Linear RGB back to the six hex digits a swatch or a CSS value wants. */
export function toHex(colour: Rgb): string {
  const channel = (value: number): string =>
    Math.round(linearToSrgb(value) * 255)
      .toString(16)
      .padStart(2, '0');
  return `${channel(colour.r)}${channel(colour.g)}${channel(colour.b)}`;
}

/**
 * A deterministic sparkle field: 0 or 1 at integer pixel coordinates.
 *
 * An integer hash and not `Math.random`, for the reason in the module note:
 * the sparkle has to be a property of the *nail*, not of the frame, or the
 * glitter boils as the hand holds still. The constants are the usual
 * odd-multiplier mix; nothing depends on their particular values beyond
 * their being odd and large.
 */
export function sparkleAt(x: number, y: number, density: number): number {
  if (density <= 0) {
    return 0;
  }
  let hash = (Math.trunc(x) * 0x1f1f1f1f) ^ (Math.trunc(y) * 0x3b9aca07);
  hash = Math.imul(hash ^ (hash >>> 15), 0x2c1b3c6d);
  hash = Math.imul(hash ^ (hash >>> 12), 0x297a2d39);
  const unit = ((hash ^ (hash >>> 15)) >>> 0) / 0x1_0000_0000;
  return unit < density ? 1 : 0;
}

/** Everything the shading function needs about one pixel of one nail. */
export interface Surface {
  /** Across the nail, −1 at one edge and +1 at the other. */
  u: number;
  /** Along the nail, 0 at the cuticle and 1 at the free edge. */
  v: number;
  /** The nail's unit axis, from `geometry.nail`. */
  axis: Point;
  /** Pixel coordinates, for the sparkle field. */
  x: number;
  y: number;
}

/**
 * The nail's surface normal at `u`, in the nail's own 2-D frame.
 *
 * A nail is a cylinder section: it curves hard across its width and barely at
 * all along its length. `CURVE` is how far the edges turn away from the
 * viewer — 55° is a fingernail, and it is what puts the highlight in a stripe
 * down the middle instead of covering the whole nail.
 *
 * The returned vector is `{ across, along, out }`, where `out` faces the
 * camera.
 */
export const CURVE = (55 * Math.PI) / 180;

export function nailNormal(u: number): { across: number; out: number } {
  const angle = Math.max(Math.min(u, 1), -1) * CURVE;
  return { across: Math.sin(angle), out: Math.cos(angle) };
}

/**
 * One pixel of a rendered nail.
 *
 * Lambert for the body, Blinn-Phong for the highlight, both in linear light.
 * Not a physically-based BRDF: a GGX lobe per pixel is beyond a ₹15 000
 * phone's budget at 10 FPS, and the thing a client is judging is whether the
 * *colour* is right, which the diffuse term carries.
 *
 * `light` is where the light is coming from in the nail's frame, as
 * `{across, out}`, already normalised. Kiosk mode (T7) fixes it; a phone
 * estimates it from the frame.
 */
export function shadePixel(
  base: Rgb,
  finish: Finish,
  surface: Surface,
  light: { across: number; out: number } = { across: -0.35, out: 0.94 },
): Rgb {
  const normal = nailNormal(surface.u);
  const lambert = Math.max(normal.across * light.across + normal.out * light.out, 0);

  // The half vector between the light and the viewer, who is straight ahead.
  const halfAcross = light.across;
  const halfOut = light.out + 1;
  const halfLength = Math.hypot(halfAcross, halfOut);
  let cosine = (normal.across * halfAcross + normal.out * halfOut) / halfLength;

  // Cat-eye: the highlight is squeezed across the nail and smeared along it,
  // which is what a magnet does to the iron pigment. `anisotropy` is how far
  // along the axis the smear reaches; at 0 this is an ordinary round highlight.
  if (finish.anisotropy > 0) {
    const alongBand = 1 - finish.anisotropy * Math.abs(surface.v - 0.5) * 2;
    cosine *= Math.max(alongBand, 0);
  }

  const exponent = 2 / Math.max(finish.roughness, 0.001) ** 2;
  const highlight = finish.specular * Math.max(cosine, 0) ** exponent;
  const sparkle = sparkleAt(surface.x, surface.y, finish.sparkleDensity) * 0.8;

  // Ambient keeps an unlit edge from going to pure black: a nail in a salon
  // is never in a void, and a black edge reads as a hole in the hand.
  const lit = 0.25 + 0.75 * lambert;
  const add = highlight + sparkle;
  return {
    r: base.r * finish.albedo * lit + add,
    g: base.g * finish.albedo * lit + add,
    b: base.b * finish.albedo * lit + add,
  };
}

/**
 * The finishes a salon has switched on, filtered to the five that exist.
 *
 * An unknown name in the setting is dropped rather than thrown on: the
 * setting is a JSON column a future BP may extend, and a try-on that refuses
 * to open because somebody added `"holographic"` is worse than one that does
 * not offer it.
 */
export function enabledFinishes(setting: readonly string[] | undefined): readonly FinishName[] {
  if (setting === undefined) {
    return DEFAULT_ON;
  }
  const chosen = FINISHES.filter((name) => setting.includes(name));
  // Never empty: a salon that switched everything off still has to be able
  // to show a shade, and cream is the finish most of its shelf actually is.
  return chosen.length > 0 ? chosen : ['cream'];
}

/**
 * BP-12's measured `shade_colour.finish` mapped onto one of ours.
 *
 * BP-12 records what the *pigment* does, from a spectrophotometer, in its own
 * vocabulary. Anything it names that T5 has no renderer for falls back to
 * cream, which is the most common finish and the least wrong guess.
 */
export function finishFor(measured: string, enabled: readonly FinishName[]): FinishName {
  const direct = FINISHES.find((name) => name === measured);
  if (direct !== undefined && enabled.includes(direct)) {
    return direct;
  }
  return enabled.includes('cream') ? 'cream' : (enabled[0] ?? 'cream');
}
